import { useRef, useState } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/clients/trpcClient";
async function compressAndUpload(file) {
  const {
    default: imageCompression
  } = await import("browser-image-compression");
  const compressed = await imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    fileType: "image/webp"
  });
  const buffer = await compressed.arrayBuffer();
  const base64 = btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ""));
  const result = await trpcClient.uploads.upload.mutate({
    fileData: base64,
    contentType: "image/webp",
    fileName: `${Date.now()}-${file.name.replace(/\.[^.]+$/, "")}.webp`,
    folder: "experiences",
    bucket: "upplevelser"
  });
  return result.url;
}
export function useMediaManager(items, onChange) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState([]);
  const openFilePicker = () => inputRef.current?.click();
  const handleFiles = async files => {
    const fileArray = Array.from(files);
    const previews = fileArray.map(f => ({
      id: `${Date.now()}-${f.name}`,
      previewUrl: URL.createObjectURL(f),
      name: f.name
    }));
    setUploading(prev => [...prev, ...previews]);
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const preview = previews[i];
      try {
        const url = await compressAndUpload(file);
        // Functional update: items may be removed or edited while uploads run
        onChange(current => [...current, {
          url,
          altText: file.name.replace(/\.[^.]+$/, ""),
          isPrimary: current.length === 0
        }]);
      } catch {
        toast.error(`Kunde inte ladda upp ${file.name}`);
      } finally {
        setUploading(prev => prev.filter(u => u.id !== preview.id));
        URL.revokeObjectURL(preview.previewUrl);
      }
    }
  };
  const remove = index => {
    const wasPrimary = items[index]?.isPrimary;
    const updated = items.filter((_, i_0) => i_0 !== index);
    if (wasPrimary && updated.length > 0) {
      updated[0] = {
        ...updated[0],
        isPrimary: true
      };
    }
    onChange(updated);
  };
  const setPrimary = index_0 => {
    onChange(items.map((item, i_1) => ({
      ...item,
      isPrimary: i_1 === index_0
    })));
  };
  const setAltText = (index_1, altText) => {
    const updated_0 = [...items];
    updated_0[index_1] = {
      ...updated_0[index_1],
      altText
    };
    onChange(updated_0);
  };
  const onDragEnter = e => {
    e.preventDefault();
    setDragActive(true);
  };
  const onDragLeave = e_0 => {
    e_0.preventDefault();
    setDragActive(false);
  };
  const onDragOver = e_1 => e_1.preventDefault();
  const onDrop = e_2 => {
    e_2.preventDefault();
    setDragActive(false);
    if (e_2.dataTransfer.files.length > 0) {
      handleFiles(e_2.dataTransfer.files);
    }
  };
  return {
    inputRef,
    dragActive,
    uploading,
    openFilePicker,
    handleFiles,
    remove,
    setPrimary,
    setAltText,
    dragHandlers: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop
    }
  };
}